import React, { ReactNode, useMemo, useState } from "react";
import { Box, MenuItem, Select, SelectChangeEvent, Tooltip, Typography } from "@mui/material";

import { useParams } from "next/navigation";
import { t, Trans } from "@lingui/macro";
import ProductSingle from "@/components/core/product-single";
import GridSvg from "@/components/svg/category/grid";
import ListSvg from "@/components/svg/category/list";
import { useLingui } from "@lingui/react";
import AlertComponent from "@/components/core/alert";
import { useAlertStore } from "@/components/context/alert-store";
import PaginationComponent from "@/components/core/pagination-component";

interface ISortOption {
  value: string;
  label: ReactNode;
}

interface ProductItem {
  name: string;
  id: number;
  subtitle: string;
  type: string;
  original_image: string;
  seo_url: string;
  price: number;
  seo_urls: object;
  special: number;
}

const sortOptions = [
  { value: "default", label: <Trans>Default</Trans> },
  { value: "price.asc", label: <Trans>Price (low - high)</Trans> },
  { value: "price.desc", label: <Trans>Price (high - low)</Trans> },
  { value: "name.asc", label: <Trans>Name (A - Z)</Trans> },
  { value: "name.desc", label: <Trans>Name (Z - A)</Trans> },
];

const itemsPerPageOptions = [10, 25, 50, 75, 100];
type GRID_STYLE = "grid" | "list";

const BrandProductsList: React.FC<{ products: ProductItem[]; manufacturerName: string }> = ({
  products,
  manufacturerName,
}) => {
  const { lang } = useParams<{ lang: string }>();
  const { message, setMessage } = useAlertStore();

  const [itemsToDisplay, setItemsToDisplay] = useState<number>(10);
  const [sortOrder, setSortOrder] = useState<string>("default");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [mobileGridStyle, setMobileGridStyle] = useState<GRID_STYLE>("grid");
  const { i18n } = useLingui();

  const sortedItems = useMemo(() => {
    const items = products || [];
    return [...items].sort((a, b) => {
      switch (sortOrder) {
        case "name.asc":
          return a.name.localeCompare(b.name);
        case "name.desc":
          return b.name.localeCompare(a.name);
        case "price.asc":
          return a.price - b.price;
        case "price.desc":
          return b.price - a.price;
        default:
          return 0;
      }
    });
  }, [products, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(sortedItems.length / itemsToDisplay) || 0,
    [sortedItems.length, itemsToDisplay],
  );

  const startIndex = (currentPage - 1) * itemsToDisplay;

  const displayedItems = useMemo(
    () => sortedItems.slice(startIndex, startIndex + itemsToDisplay),
    [sortedItems, startIndex, itemsToDisplay],
  );

  if (!products.length) {
    return (
      <Typography variant="h6">
        <Trans>No products available for this brand.</Trans>
      </Typography>
    );
  }
  const handleItemsChange = (event: SelectChangeEvent<number>) => {
    setItemsToDisplay(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    setSortOrder(event.target.value);
    setCurrentPage(1);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: "15px", fontSize: "20px", lineHeight: "1.1" }}>
        {manufacturerName}
      </Typography>
      {message && <AlertComponent message={message} setMessage={setMessage} />}
      {products.length > 0 && (
        <>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "space-between",
              mt: "30px",
              mb: "20px",
            }}
          >
            <Box
              sx={{
                minWidth: 150,
                marginRight: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                <Trans>Show</Trans>
              </Typography>
              <Select
                value={itemsToDisplay}
                onChange={handleItemsChange}
                sx={{
                  borderRadius: "8px",
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                }}
              >
                {itemsPerPageOptions.map((number) => (
                  <MenuItem key={number} value={number} sx={{ borderBottom: 0, p: "10px" }}>
                    {number}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box
              sx={{
                minWidth: 150,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                <Trans>Sort Order</Trans>
              </Typography>
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                sx={{
                  width: "315px",
                  borderRadius: "8px",
                  "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderWidth: "1px",
                    borderColor: "rgba(0, 0, 0, 0.23)",
                  },
                }}
              >
                {sortOptions.map((option: ISortOption) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ borderBottom: 0, p: "10px" }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "space-between",
              mt: "30px",
              mb: "20px",
              borderTop: "1px solid #dfdfe0",
              borderBottom: "1px solid #dfdfe0",
              width: "100%",
              py: 1,
            }}
          >
            <Box
              sx={{
                width: "50%",
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderRight: "1px solid #dfdfe0",
              }}
            >
              <Select
                value={sortOrder}
                onChange={handleSortChange}
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              >
                {sortOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{ borderBottom: 0, p: "10px" }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-evenly", width: "50%" }}>
              <Tooltip title={t(i18n)`Grid`} arrow placement="top">
                <Box sx={{ cursor: "pointer" }} onClick={() => setMobileGridStyle("grid")}>
                  <GridSvg active={mobileGridStyle === "grid"} />
                </Box>
              </Tooltip>
              <Tooltip title={t(i18n)`List`} arrow placement="top">
                <Box sx={{ cursor: "pointer" }} onClick={() => setMobileGridStyle("list")}>
                  <ListSvg active={mobileGridStyle === "list"} />
                </Box>
              </Tooltip>
            </Box>
          </Box>
        </>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns:
            mobileGridStyle === "grid"
              ? {
                  xs: "repeat(auto-fill, minmax(160px, 1fr))",
                  md: "repeat(auto-fill, minmax(240px, 1fr))",
                }
              : { xs: "1fr" },
          columnGap: "15px",
          rowGap: "36px",
        }}
      >
        {displayedItems?.map((product: ProductItem) => (
          <ProductSingle
            lang={lang}
            mobileGridStyle={mobileGridStyle}
            key={product.id}
            item={product}
          />
        ))}
      </Box>

      {products.length > 0 && (
        <PaginationComponent
          totalPages={totalPages}
          currentPage={currentPage}
          itemsToDisplay={itemsToDisplay}
          totalItems={products.length}
          setCurrentPage={setCurrentPage}
        />
      )}
    </Box>
  );
};
export default BrandProductsList;
